"use client";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/app/_module/services/api";
import { endpoint } from "@/app/_module/services/endpoints";
import { getApiUrl } from "@/app/_module/services/config";
import { useToast } from "@/app/_module/hooks/use-toast";

export interface ContactCardData {
  firstName: string;
  lastName: string;
  phone: string;
  email?: string;
  company?: string;
  title?: string;
  website?: string;
}

interface ContactCardResponse {
  contactCardVcfUrl: string | null;
  contactCardData: ContactCardData | null;
}

export const contactCardQueryKeys = {
  all: ["contactCard"] as const,
  data: () => [...contactCardQueryKeys.all, "data"] as const,
};

export function useContactCard() {
  return useQuery({
    queryKey: contactCardQueryKeys.data(),
    queryFn: async (): Promise<ContactCardResponse> => {
      const response = await api.get<any>({
        url: getApiUrl(endpoint.communication.contactCard),
        auth: true,
      });
      return response.data?.data || response.data;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
  });
}

export function useSaveContactCard() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: { contactCardData: ContactCardData; vcfUrl: string }) => {
      const response = await api.put<any>({
        url: getApiUrl(endpoint.communication.contactCard),
        body: data,
        auth: true,
      });
      return response.data?.data || response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contactCardQueryKeys.all });
      toast({
        title: "Contact Card Saved",
        description: "Your contact card has been generated and saved.",
      });
    },
    onError: () => {
      toast({
        title: "Save Failed",
        description: "Failed to save contact card. Please try again.",
        variant: "destructive",
      });
    },
  });
}
